export const en = {
  app: {
    name: 'SatrPDF',
    shortName: 'SatrPDF',
    tagline: 'Private PDF tools, on your device.',
    heroTitle: 'PDF tools that stay on your device',
    heroDescription: 'Edit, convert, protect, and understand PDFs privately—even offline.',
    privacyPromise: 'Your files never leave this device.',
    localProcessing: 'Everything is processed locally in your browser.',
    loading: 'Loading SatrPDF…',
  },
  common: {
    actions: {
      add: 'Add',
      addFiles: 'Add files',
      addMoreFiles: 'Add more files',
      apply: 'Apply',
      back: 'Back',
      cancel: 'Cancel',
      chooseFile: 'Choose file',
      chooseFiles: 'Choose files',
      clear: 'Clear',
      close: 'Close',
      confirm: 'Confirm',
      continue: 'Continue',
      copy: 'Copy',
      delete: 'Delete',
      download: 'Download',
      downloadAll: 'Download all',
      downloadZip: 'Download all as ZIP',
      edit: 'Edit',
      install: 'Install',
      learnMore: 'Learn more',
      moveDown: 'Move down',
      moveEarlier: 'Move earlier',
      moveLater: 'Move later',
      moveUp: 'Move up',
      next: 'Next',
      open: 'Open',
      pause: 'Pause',
      preview: 'Preview',
      previous: 'Previous',
      process: 'Process locally',
      redo: 'Redo',
      remove: 'Remove',
      rename: 'Rename',
      reset: 'Reset',
      resume: 'Resume',
      retry: 'Try again',
      save: 'Save',
      search: 'Search',
      selectAll: 'Select all',
      selectNone: 'Select none',
      settings: 'Settings',
      startOver: 'Start over',
      startOverAndClear: 'Start over and clear temporary data',
      undo: 'Undo',
      update: 'Update',
    },
    status: {
      available: 'Available',
      canceled: 'Canceled',
      canceling: 'Canceling…',
      complete: 'Complete',
      completedLocally: 'Completed locally',
      failed: 'Failed',
      installed: 'Installed',
      installing: 'Installing…',
      loading: 'Loading…',
      notInstalled: 'Not installed',
      paused: 'Paused',
      preparing: 'Preparing…',
      processing: 'Processing…',
      queued: 'Queued',
      ready: 'Ready',
      unavailable: 'Unavailable',
      validating: 'Validating result…',
    },
    labels: {
      all: 'All',
      automatic: 'Automatic',
      color: 'Color',
      currentPage: 'Current page',
      file: 'File',
      files: 'Files',
      fileName: 'Filename',
      fileOrder: 'File order',
      fileSize: 'File size',
      format: 'Format',
      from: 'From',
      height: 'Height',
      language: 'Language',
      margins: 'Margins',
      opacity: 'Opacity',
      options: 'Options',
      output: 'Output',
      outputFilename: 'Output filename',
      page: 'Page',
      pages: 'Pages',
      pageCount: 'Page count',
      pageOrder: 'Page order',
      pageRange: 'Page range',
      pageSize: 'Page size',
      password: 'Password',
      position: 'Position',
      quality: 'Quality',
      resolution: 'Resolution',
      rotation: 'Rotation',
      scale: 'Scale',
      selectedPages: 'Selected pages',
      size: 'Size',
      to: 'To',
      type: 'Type',
      units: 'Units',
      width: 'Width',
    },
    choices: {
      allPages: 'All pages',
      selectedPages: 'Selected pages',
      oddPages: 'Odd pages',
      evenPages: 'Even pages',
      portrait: 'Portrait',
      landscape: 'Landscape',
      yes: 'Yes',
      no: 'No',
      on: 'On',
      off: 'Off',
    },
    counts: {
      files_zero: 'No files',
      files_one: '{{count}} file',
      files_two: '{{count}} files',
      files_few: '{{count}} files',
      files_many: '{{count}} files',
      files_other: '{{count}} files',
      pages_zero: 'No pages',
      pages_one: '{{count}} page',
      pages_two: '{{count}} pages',
      pages_few: '{{count}} pages',
      pages_many: '{{count}} pages',
      pages_other: '{{count}} pages',
      results_zero: 'No results',
      results_one: '{{count}} result',
      results_two: '{{count}} results',
      results_few: '{{count}} results',
      results_many: '{{count}} results',
      results_other: '{{count}} results',
      differences_zero: 'No differences',
      differences_one: '{{count}} difference',
      differences_two: '{{count}} differences',
      differences_few: '{{count}} differences',
      differences_many: '{{count}} differences',
      differences_other: '{{count}} differences',
    },
    units: {
      byte: 'B',
      kilobyte: 'KB',
      megabyte: 'MB',
      gigabyte: 'GB',
      percent: '{{value}}%',
      degrees: '{{value}}°',
      dpi: '{{value}} DPI',
      millimeters: '{{value}} mm',
      points: '{{value}} pt',
    },
  },
  language: {
    names: {
      english: 'English',
      arabic: 'العربية',
    },
    firstRun: {
      headingEnglish: 'Choose your language',
      headingArabic: 'اختر لغتك',
      descriptionEnglish: 'You can change this later in Settings.',
      descriptionArabic: 'يمكنك تغيير اللغة لاحقًا من الإعدادات.',
      privacyEnglish: 'Files stay on this device.',
      privacyArabic: 'تبقى الملفات على هذا الجهاز.',
      useEnglish: 'Use English',
      useArabic: 'استخدام العربية',
    },
    switcherLabel: 'Change language',
    currentLanguage: 'Current language: {{language}}',
    changedTo: 'Language changed to {{language}}.',
  },
  navigation: {
    home: 'Home',
    allTools: 'All tools',
    favorites: 'Favorites',
    recent: 'Recently used',
    offline: 'Offline',
    settings: 'Settings',
    about: 'About',
    privacy: 'Privacy',
    skipToContent: 'Skip to main content',
    openMenu: 'Open navigation menu',
    closeMenu: 'Close navigation menu',
    breadcrumbs: 'Breadcrumbs',
  },
  home: {
    searchLabel: 'Search PDF tools',
    searchPlaceholder: 'Search 33 PDF tools',
    searchHint: 'Search by task, format, or tool name.',
    noSearchResults: 'No tools match “{{query}}”.',
    clearSearch: 'Clear search',
    categoriesTitle: 'All PDF tools',
    categoriesDescription: 'Choose a tool. Your document stays on this device from start to finish.',
    favoritesTitle: 'Favorite tools',
    favoritesEmpty: 'Mark tools as favorites to keep them here.',
    recentTitle: 'Recently used tools',
    recentEmpty: 'Tools you use will appear here. Filenames and document details are never stored.',
    addFavorite: 'Add {{tool}} to favorites',
    removeFavorite: 'Remove {{tool}} from favorites',
    privacyTitle: 'Private by design',
    privacyDescription: 'No uploads, accounts, analytics, or document-processing servers.',
    offlineTitle: 'Set up offline use',
    offlineDescription: 'Core tools work offline after the app is cached. Install OCR and local model packs only when you need them.',
    browsePacks: 'Browse offline packs',
  },
  categories: {
    organize: 'Organize PDF',
    optimize: 'Optimize PDF',
    toPdf: 'Convert to PDF',
    fromPdf: 'Convert from PDF',
    edit: 'Edit PDF',
    security: 'PDF security',
    intelligence: 'PDF intelligence',
  },
  toolLibrary: {
    title: 'All tools',
    description: 'Search or browse all 33 local PDF tools.',
    toolCount: '{{count}} tools',
    requiresPack: 'Requires an offline pack',
    worksOffline: 'Works offline',
    favorite: 'Favorite',
    recentlyUsed: 'Recently used',
    acceptedFormats: 'Accepted formats',
    outputFormats: 'Output formats',
    multipleFiles: 'Supports multiple files',
    limitation: 'Capability note',
  },
  workflow: {
    steps: {
      files: 'Files',
      pages: 'Pages',
      options: 'Options',
      process: 'Process',
      result: 'Result',
      stepOf: 'Step {{current}} of {{total}}: {{name}}',
    },
    upload: {
      title: 'Choose your files',
      titlePdf: 'Choose PDF files',
      dropPrompt: 'Drop files here, or choose files',
      dropActive: 'Drop the files to add them',
      mobilePrompt: 'Choose files from this device',
      cameraPrompt: 'Use the camera or choose images',
      privacyHint: 'Files are read only on this device.',
      acceptedTypes: 'Accepted: {{types}}',
      multipleAllowed: 'You can add multiple files.',
      oneFileAllowed: 'Choose one file.',
      passwordProtected: 'Password protected',
      scanning: 'Checking {{name}}…',
    },
    fileList: {
      title: 'Selected files',
      empty: 'No files selected.',
      addAnother: 'Add another file',
      removeFile: 'Remove {{name}}',
      replaceFile: 'Replace {{name}}',
      fileDetails: '{{pages}}, {{size}}',
      moveFileEarlier: 'Move {{name}} earlier',
      moveFileLater: 'Move {{name}} later',
      movedFile: 'Moved {{name}} to position {{position}} of {{total}}.',
    },
    pages: {
      title: 'Review pages',
      thumbnail: 'Page {{page}}',
      thumbnailDetails: 'Page {{page}}, {{rotation}}°, {{selection}}',
      selected: 'selected',
      notSelected: 'not selected',
      selectPage: 'Select page {{page}}',
      deselectPage: 'Deselect page {{page}}',
      selectRange: 'Select a page range',
      rangeHelp: 'Use “all”, “odd”, “even”, or ranges such as 1–3, 5, 8–10.',
      rangePlaceholder: 'all or 1–3, 5',
      rotateClockwise: 'Rotate page {{page}} clockwise',
      rotateCounterclockwise: 'Rotate page {{page}} counter-clockwise',
      deletePage: 'Delete page {{page}}',
      duplicatePage: 'Duplicate page {{page}}',
      movePageEarlier: 'Move page {{page}} earlier',
      movePageLater: 'Move page {{page}} later',
      movedPage: 'Moved page {{page}} to position {{position}} of {{total}}.',
      addBlank: 'Add blank page',
      insertFromFile: 'Insert pages from another PDF',
    },
    options: {
      title: 'Choose options',
      required: 'Required',
      optional: 'Optional',
      resetAll: 'Reset all options',
      automaticFilename: 'Leave blank to create a filename automatically.',
      outputFilenameHelp: 'The file extension is added automatically.',
    },
    password: {
      title: 'Enter the PDF password',
      description: 'The password is used only in memory and is never saved.',
      currentPassword: 'Current password',
      openPassword: 'Open password',
      permissionsPassword: 'Permissions password',
      confirmPassword: 'Confirm password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      neverStored: 'Passwords are never stored or transmitted.',
    },
    processing: {
      title: 'Processing on this device…',
      keepOpen: 'Keep this tab open until processing finishes.',
      preparingFiles: 'Preparing files…',
      loadingEngine: 'Loading the local processing engine…',
      processingFile: 'Processing {{name}}…',
      processingFileNumber: 'Processing file {{current}} of {{total}}…',
      processingPage: 'Processing page {{current}} of {{total}}…',
      finalizing: 'Creating the result…',
      validating: 'Reopening and validating the result…',
      cleaning: 'Clearing temporary working data…',
      cancel: 'Cancel processing',
      cancelConfirmTitle: 'Cancel processing?',
      cancelConfirmDescription: 'The partial result will be discarded and temporary working data will be cleared.',
    },
    result: {
      title: 'Your file is ready',
      multipleTitle: 'Your files are ready',
      validated: 'The result reopened successfully.',
      validationWarning: 'The result was created but could not be fully validated.',
      originalSize: 'Original size',
      resultSize: 'Result size',
      spaceSaved: 'Space saved',
      savedAmount: 'Saved {{size}} ({{percent}}%)',
      noReduction: 'This PDF is already efficiently compressed; the result may not be smaller.',
      previewResult: 'Preview result',
      downloadResult: 'Download result',
      downloadNamed: 'Download {{name}}',
      clearTitle: 'Clear this task?',
      clearDescription: 'Selected files, previews, object URLs, and temporary data for this task will be removed.',
      cleared: 'The current task and its temporary data were cleared.',
    },
    batch: {
      title: 'Batch processing',
      filesQueued: '{{count}} files queued',
      completed: '{{complete}} of {{total}} complete',
      failed: '{{count}} failed',
      downloadSuccessful: 'Download successful results',
    },
  },
  offline: {
    connectivity: {
      title: 'Connectivity',
      online: 'Online',
      offline: 'Offline',
      onlineDescription: 'The app is online. Files still remain on this device.',
      offlineDescription: 'The app is offline. Installed tools and packs remain available.',
    },
    readiness: {
      title: 'Offline readiness',
      ready: 'Ready offline',
      coreReady: 'Core tools are ready offline.',
      notReady: 'Offline setup is incomplete',
      notReadyDescription: 'Finish caching the app before going offline.',
      checking: 'Checking offline readiness…',
    },
    installApp: {
      title: 'Install SatrPDF',
      description: 'Install the app for quicker access and a more reliable offline experience.',
      action: 'Install app',
      installed: 'App installed',
      unavailable: 'Installation is not available in this browser.',
    },
    packs: {
      title: 'Offline packs',
      description: 'OCR and local AI features use optional same-origin packs. Install each pack once, then use it offline.',
      downloadAll: 'Download everything needed for offline use',
      removeAll: 'Remove all optional packs',
      englishOcr: 'English OCR pack',
      arabicOcr: 'Arabic OCR pack',
      translation: 'English–Arabic translation pack',
      summarization: 'Summarization pack',
      downloadSize: 'Download size',
      installedSize: 'Installed size',
      deviceRequirements: 'Device requirements',
      supportedFeatures: 'Supported features',
      installPack: 'Install {{name}}',
      removePack: 'Remove {{name}}',
      installProgress: 'Installing {{name}}: {{percent}}%',
      installedPack: '{{name}} is installed and ready offline.',
      missingForTool: '{{tool}} needs {{pack}}.',
      finishBeforeOffline: 'This pack must finish downloading before you go offline.',
      removalNote: 'Removing a pack does not remove your documents because documents are never stored with model packs.',
    },
    storage: {
      title: 'Offline storage',
      description: 'Manage the app shell, optional models, and temporary browser storage.',
      used: '{{used}} used by SatrPDF',
      usedOfQuota: '{{used}} used of approximately {{quota}} available',
      estimating: 'Calculating storage use…',
      unavailable: 'Storage estimates are unavailable in this browser.',
      persistent: 'Persistent storage is enabled.',
      notPersistent: 'The browser may remove cached files when storage is low.',
      requestPersistent: 'Keep offline files available',
      clearModelsAndTemporary: 'Clear cached models and temporary files',
      clearTitle: 'Clear cached models and temporary files?',
      clearDescription: 'Optional OCR and AI packs will need to be downloaded again. User documents are not part of these caches.',
      cleared: 'Cached models and temporary files were cleared.',
    },
    update: {
      available: 'An update is ready',
      description: 'Reload when you are finished with the current task. Reloading clears its temporary data.',
      reload: 'Reload to update',
      later: 'Later',
      current: 'SatrPDF is up to date.',
    },
  },
  settings: {
    title: 'Settings',
    description: 'Manage language, offline files, privacy, accessibility, and app updates.',
    sections: {
      general: 'General',
      offlineStorage: 'Offline & storage',
      privacyData: 'Privacy & local data',
      updates: 'Updates',
      accessibility: 'Accessibility',
      about: 'About & licenses',
    },
    general: {
      language: 'Language',
      languageDescription: 'Changing language immediately updates the interface direction without clearing the current task.',
    },
    privacy: {
      title: 'Privacy & local data',
      filesStayLocal: 'Files stay on this device',
      filesStayLocalDescription: 'Document processing does not use uploads, APIs, analytics, background sync, or remote conversion services.',
      recentTools: 'Recently used tools',
      recentToolsDescription: 'Only tool IDs and timestamps are stored. Filenames and document details are not stored.',
      favorites: 'Favorite tools',
      favoritesDescription: 'Favorite tool IDs are stored only in this browser.',
      savedSignatures: 'Saved signatures',
      savedSignaturesDescription: 'Signatures are saved only after explicit consent and remain on this device.',
      manageSignatures: 'Manage saved signatures',
      clearCurrentDocument: 'Clear current document',
      clearAllData: 'Clear all local data',
      clearAllTitle: 'Clear all SatrPDF data from this browser?',
      clearAllDescription: 'This removes preferences, favorites, history, saved signatures, cached packs, and temporary data. Installed app files may remain until the browser clears them.',
      clearAllConfirm: 'Clear all local data',
      clearAllComplete: 'All local SatrPDF data was cleared.',
    },
    updates: {
      title: 'App updates',
      version: 'Version',
      check: 'Check for updates',
      checking: 'Checking for updates…',
      lastChecked: 'Last checked {{time}}',
    },
    accessibility: {
      title: 'Accessibility',
      motion: 'Motion',
      motionDescription: 'Reduce non-essential animation and transitions.',
      followSystem: 'Follow system setting',
      reducedMotion: 'Reduce motion',
      keyboardHelp: 'Keyboard help',
      keyboardHelpDescription: 'All file, page, and editor actions provide non-drag keyboard controls.',
    },
    about: {
      title: 'About SatrPDF',
      description: 'A bilingual, offline-first PDF toolkit that runs entirely in the browser.',
      privacyModel: 'Privacy model',
      capabilityMatrix: 'PDF capability matrix',
      thirdPartyNotices: 'Third-party notices',
      sourceLicenses: 'Open-source licenses',
      license: 'License and warranty',
      licenseDescription: 'Copyright © 2026 SatrPDF contributors. Free software under AGPL-3.0-or-later; provided without warranty.',
      sourceCode: 'Corresponding source',
      sourceCodeDescription: 'Get the exact application and MuPDF source and build scripts for this version.',
      version: 'Version {{version}}',
    },
  },
  notices: {
    localOnly: 'Processing happens on this device. Keep this tab open.',
    officeBestEffort: 'Complex {{format}} layouts may change. Review the result before sharing it.',
    editableApproximation: 'Editable output may not match the original layout exactly.',
    visualSignature: 'This adds a visual signature. It is not a certificate-backed digital signature.',
    permissionEnforcement: 'Some PDF readers may ignore permission restrictions.',
    permanentRedaction: 'Redaction is permanent after you apply it. Keep the original file.',
    pdfaValidation: 'Compliance is reported only after local validation passes.',
    aiFallback: 'This device is using an extractive summary because it cannot run the full local model.',
    translatedLayout: 'The translated layout is a best-effort reconstruction. Review it before use.',
    croppedContent: 'Cropping changes the visible page area; content outside it may remain in the file.',
    strongCompression: 'Strong compression may reduce text accessibility and image quality.',
  },
  errors: {
    title: 'Something went wrong',
    generic: 'The task could not be completed. Your original file was not changed.',
    unexpected: 'An unexpected error occurred.',
    tryAgain: 'Try again. If the problem continues, use fewer or smaller files.',
    file: {
      missing: 'Choose a file to continue.',
      tooFew: 'Add at least {{minimum}} files to continue.',
      tooMany: 'This tool accepts no more than {{maximum}} files at once.',
      empty: '{{name}} is empty.',
      unsupportedType: '{{name}} is not a supported file type.',
      extensionMismatch: '{{name}} does not match its file extension.',
      invalidPdf: '{{name}} is not a valid PDF.',
      damagedPdf: 'We couldn’t open {{name}}. It may be damaged or use an unsupported PDF feature.',
      encrypted: '{{name}} is password protected.',
      tooLarge: '{{name}} may be too large for this device.',
      duplicate: '{{name}} has already been added.',
      archiveUnsafe: '{{name}} contains too many or unusually large archived items and cannot be opened safely.',
    },
    password: {
      required: 'Enter the password to continue.',
      incorrect: 'The password is incorrect.',
      mismatch: 'The passwords do not match.',
      weak: 'Choose a longer, less predictable password.',
      samePasswords: 'Use different open and permissions passwords.',
      unsupportedEncryption: 'This PDF uses an encryption method this browser cannot open.',
    },
    pages: {
      required: 'Select at least one page.',
      invalidRange: 'Enter a valid page range.',
      rangeHelp: 'Use a range such as 1–3, 5, 8–10.',
      outOfRange: 'The page range is outside this document.',
      reversedRange: 'Page ranges must start with the earlier page.',
      duplicateRange: 'This selection contains repeated pages.',
      overlappingRanges: 'These page ranges overlap.',
      removeAll: 'At least one page must remain in the PDF.',
      cropOutsidePage: 'The crop area must remain inside the page.',
    },
    memory: {
      title: 'Not enough memory',
      description: 'This file may be too large for this device. Close other tabs or process fewer pages.',
      storage: 'There is not enough browser storage to complete this task.',
      model: 'This device does not have enough memory for the selected local model.',
    },
    processing: {
      failed: 'Local processing failed.',
      workerStopped: 'The processing worker stopped unexpectedly.',
      canceled: 'Processing was canceled and partial output was discarded.',
      timeout: 'Processing took too long and was stopped to protect the browser.',
      malformedInput: 'The file contains malformed data that could not be processed safely.',
      outputInvalid: 'The result was created but failed validation, so it will not be offered as successful output.',
      unsupportedFeature: 'This document uses a feature that the local processing engine does not support.',
    },
    offline: {
      packRequired: '{{tool}} requires {{pack}}.',
      packNotInstalled: 'Install the required offline pack before using this tool.',
      packDownloadFailed: 'The offline pack could not be downloaded.',
      packCorrupt: 'The installed pack could not be verified. Remove it and install it again.',
      coreNotReady: 'This tool is not ready offline yet. Reconnect and finish offline setup.',
      unexpectedlyOffline: 'The connection was lost before the required local asset finished downloading.',
    },
    storage: {
      unavailable: 'Local browser storage is unavailable.',
      writeFailed: 'Temporary data could not be saved locally.',
      clearFailed: 'Some local data could not be cleared.',
      quotaExceeded: 'Browser storage is full. Remove optional packs or clear temporary files.',
    },
    download: {
      failed: 'The download could not be started.',
      blocked: 'The browser blocked the download. Allow downloads and try again.',
      zipFailed: 'The ZIP archive could not be created.',
    },
    ocr: {
      failed: 'Text recognition failed on page {{page}}.',
      noText: 'No readable text was detected.',
      lowConfidence: 'Text was detected with low confidence. Review the output carefully.',
      languageMissing: 'Install the {{language}} OCR pack to continue.',
    },
    browser: {
      unsupported: 'This browser does not support a feature required by this tool.',
      cameraDenied: 'Camera access was denied. You can choose existing images instead.',
      cameraUnavailable: 'No compatible camera is available. You can choose existing images instead.',
      webGpuUnavailable: 'The full local model is unavailable on this device.',
      serviceWorkerUnavailable: 'This browser cannot make the app reliably available offline.',
    },
  },
  a11y: {
    dialogOpened: 'Dialog opened',
    dialogClosed: 'Dialog closed',
    loadingRegion: 'Loading content',
    progressLabel: '{{task}} progress',
    progressValue: '{{percent}} percent complete',
    preview: 'Document preview',
    pagePreview: 'Preview of page {{page}}',
    thumbnailList: 'Page thumbnails',
    fileList: 'Selected file list',
    optionsPanel: 'Tool options',
    resultRegion: 'Processing result',
    statusMessage: 'Status: {{status}}',
    errorSummary: 'There are problems that need attention.',
    directionalIcon: 'Directional icon',
    externalTechnicalTerm: '{{term}}, technical format name',
  },
} as const;

export type EnglishTranslations = typeof en;
